import { connect } from 'react-redux';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import {
  INBOX_MESSAGE_ITEM_TYPE,
  MESSAGE_REACTION_ADDED,
} from '@core/constants/analytics';
import {
  hasCurrentUserReacted,
  reactionForItem,
} from '@core/reactions/selectors';
import { analyticsTrackEvent } from '@core/utils/analytics';
import { user } from '@core/selectors/users';
import {
  selectorsResponsive as selectors,
  operationsResponsive as creators,
} from '../../../../store/conversations';
import {
  selectors as notificationSelectors,
  operations as notificationOperations,
} from '../../../../store/notifications';
import { operations as reactionOperations } from '../../../../store/reactions';
import ConversationModalWithInvite from './ConversationModalWithInvite';

const getStatus = (state, participants, currentUser) => {
  if (!participants) return null;
  const participantIds = Object.keys(participants);
  const others = participantIds?.filter((id) => id !== currentUser.objectId);
  if (others.length === 1) {
    const participant = user(state, others[0]);

    return participant?.status;
  }

  return null;
};

const makeMapStateToProps = () => {
  return (state, { flags }) => {
    const isFetchingGroups = false;
    const groupId = selectors.currentConversationId(state);
    const { conversations } = state.data.inboxv2;
    const conversation = conversations[groupId];
    const currentUser = state.user.user;
    const status = flags.ftUserStatusEnabled202107
      ? getStatus(state, conversation?.participants, currentUser)
      : null;

    return {
      groupId,
      currentUser,
      conversation,
      leaveGroupOpened: state.inboxv2.leaveGroupOpened,
      leavingConversation: state.inboxv2.leavingConversation,
      open: !!groupId && !isFetchingGroups,
      renameGroupOpened: state.inboxv2.renameGroupOpened,
      title: selectors.conversationTitle(state, groupId),
      status,
      conversationMuted: notificationSelectors.isConversationMuted(
        state,
        groupId
      ),
      hasFetchedNotificationSettings:
        notificationSelectors.hasFetchedSettings(state),
      viewParticipantsOpened: state.inboxv2.viewParticipantsOpened,
      addParticipantOpened: state.inboxv2.addParticipantOpened,
    };
  };
};

const mapDispatchToProps = (dispatch) => ({
  openAddParticipant: () => dispatch(creators.openViewParticipants(true)),
  closeLeaveGroup: () => dispatch(creators.closeLeaveGroup()),
  openLeaveGroup: () => dispatch(creators.openLeaveGroup()),
  setConversationClosed: (conversationId) => {
    dispatch(creators.setConversationClosed(conversationId));
  },
  setConversationReopened: (conversationId) => {
    dispatch(creators.setConversationReopened(conversationId));
  },
  openRenameGroup: () => dispatch(creators.openRenameGroup()),
  openViewParticipants: () => dispatch(creators.openViewParticipants()),
  leaveConversation: () => dispatch(creators.leaveConversation()),
  toggleReaction: ({ itemId, emojiName }) => {
    // eslint-disable-next-line no-shadow
    dispatch(async (dispatch, getState) => {
      const state = getState();
      const existingReaction = reactionForItem(state, {
        itemId,
        itemType: INBOX_MESSAGE_ITEM_TYPE,
        emojiName,
      });

      // Determines if this is a new reaction (not a toggle-off of an existing one)
      // by checking whether the current user has already reacted with this emoji.
      // Used to fire the analytics event only when a reaction is being added, not removed.
      const isNewlySelectedEmoji = !hasCurrentUserReacted(
        state,
        existingReaction
      );

      await dispatch(
        reactionOperations.toggleReaction({
          itemId,
          itemType: INBOX_MESSAGE_ITEM_TYPE,
          emojiName,
        })
      );

      if (isNewlySelectedEmoji) {
        analyticsTrackEvent(MESSAGE_REACTION_ADDED);
      }
    });
  },
  fetchMessages: () => dispatch(creators.fetchMessages({ replace: true })),
  setConversationRead: (conversationId) => {
    return dispatch(
      creators.setConversationRead(new Date().toISOString(), conversationId)
    );
  },
  toggleMuteConversation: (conversationId) => {
    return dispatch(
      notificationOperations.toggleMuteConversation(conversationId)
    );
  },
  fetchNotificationSettings: () =>
    dispatch(notificationOperations.fetchNotificationSettings()),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  // Home dialog passes handleCloseModal; inbox/messages routes pass closeModal.
  const closeModal = ownProps.closeModal || ownProps.handleCloseModal;

  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    leaveConversation: async () => {
      await dispatchProps.leaveConversation();
      dispatchProps.closeLeaveGroup();
      if (closeModal) {
        closeModal();
      }
    },
    setConversationClosed: () => {
      // Use the groupId from stateProps
      if (stateProps.groupId) {
        dispatchProps.setConversationClosed(stateProps.groupId);
        // Navigate back to the conversations list after closing
        if (closeModal) {
          closeModal();
        }
      }
    },
    setConversationReopened: (conversationId) => {
      // If conversationId is provided, use it, otherwise use the groupId from stateProps
      const id = conversationId || stateProps.groupId;
      if (id) {
        dispatchProps.setConversationReopened(id);
        // Don't close the modal when unarchiving a conversation
        // This allows the user to continue the conversation
      }
    },
    setConversationRead: () => {
      if (stateProps.groupId) {
        dispatchProps.setConversationRead(stateProps.groupId);
      }
    },
    toggleMuteConversation: () => {
      if (stateProps.groupId) {
        dispatchProps.toggleMuteConversation(stateProps.groupId);
      }
    },
  };
};

export default withLDConsumer()(
  connect(
    makeMapStateToProps,
    mapDispatchToProps,
    mergeProps
  )(ConversationModalWithInvite)
);
